import { useQuery } from '@tanstack/react-query'

import { customInstance } from '@/api/client'
import {
  type AffiliateCampaignStatsResponseSchema,
  affiliateCampaignStatsResponseSchema,
} from '@/domain/campaigns'
import type { ReportGranularity } from '@/utils/report-period'

// TODO(MKT-832): replace with the generated hook once gto-wizard#2825 reaches
// dev and the affiliates Orval client is regenerated.

export type CampaignStatsParams = {
  /** Omit for all time, which is this surface's default. */
  date_from?: string
  date_to?: string
  /**
   * Only buckets `series`, which exists only under `tag_id` — so the comparison
   * table omits it rather than keying its cache on a value nothing renders.
   * The BE defaults it to monthly.
   */
  granularity?: ReportGranularity
  /** Omit for the comparison table; supply to narrow to one campaign and get `series`. */
  tag_id?: string
}

export const getCampaignStatsQueryKey = (partnerId: string, params: CampaignStatsParams) =>
  [`/v4/affiliates/admin/partners/${partnerId}/campaign-stats/`, params] as const

const fetchCampaignStats = async (
  partnerId: string,
  params: CampaignStatsParams,
  signal?: AbortSignal,
): Promise<AffiliateCampaignStatsResponseSchema> => {
  const data = await customInstance<unknown>({
    url: `/v4/affiliates/admin/partners/${partnerId}/campaign-stats/`,
    method: 'GET',
    params,
    signal,
  })

  return affiliateCampaignStatsResponseSchema.parse(data)
}

export const useCampaignStats = (partnerId: string, params: CampaignStatsParams) =>
  useQuery({
    queryKey: getCampaignStatsQueryKey(partnerId, params),
    queryFn: ({ signal }) => fetchCampaignStats(partnerId, params, signal),
  })
